import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  Assignment,
  Work,
  LocalShipping,
  AttachMoney,
  Assessment,
  AdminPanelSettings,
  ContactSupport,
  Receipt,
  ExpandLess,
  ExpandMore,

} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', roles: ['Carson_AdminUser', 'Carson_TechUser'] },
  { text: 'Customer Request Form', icon: <Assignment />, path: '/customer-request', roles: ['Carson_AdminUser', 'Carson_TechUser'] },
  { text: 'Delivery Requests', icon: <LocalShipping />, path: '/delivery-request', roles: ['Carson_AdminUser'] },  
  { text: 'Work Orders', icon: <Work />, path: '/work-order', roles: ['Carson_AdminUser'] },  
  { text: 'Effort', icon: <AttachMoney />, path: '/effort', roles: ['Carson_AdminUser'] },

  { 
    text: 'Invoice', 
    icon: <Receipt />, 
    roles: ['Carson_AdminUser'],
    subItems: [
      { text: 'Detailed Invoice', path: '/invoice/detailed' },
      { text: 'Summary Invoice', path: '/invoice/summary' }
    ]
  },
  { text: 'Reports', icon: <Assessment />, path: '/reports', roles: ['Carson_AdminUser'] },
  { text: 'Contact Us', icon: <ContactSupport />, path: '/contact', roles: ['Carson_AdminUser', 'Carson_TechUser'] },
  { 
    text: 'Admin', 
    icon: <AdminPanelSettings />, 
    roles: ['Carson_AdminUser'],
    subItems: [
      { text: 'Budget Code', path: '/admin/budget-code' },
      { text: 'Model', path: '/admin/model' },
      { text: 'Job-lines', path: '/admin/job-lines' },
      { text: 'Carson Headline', path: '/admin/carson-headline' },
      { text: 'Person In Charge', path: '/admin/person-in-charge' },
      { text: 'Sony Employee', path: '/admin/sony-employee' },
      { text: 'Temp Labor', path: '/admin/temp-labor' },
      { text: 'Email Setup', path: '/admin/email-setup' },
      { text: 'Departments', path: '/admin/departments' },
      { text: 'Work Type', path: '/admin/work-type' },
      { text: 'Budget Code Upload', path: '/admin/budget-code-upload' },
      // { text: 'Effort Upload', path: '/admin/effort-upload' },  // HIDDEN - not required
      { text: 'Model Upload', path: '/admin/model-upload' }
    ]
  },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userRole, isAdmin } = useAuth();
  const [openInvoice, setOpenInvoice] = useState(false);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [openCustomerRequest, setOpenCustomerRequest] = useState(false);

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(userRole || user?.role)
  );

  // Debug info available in development mode
  if (process.env.NODE_ENV === 'development') {
    console.log('Current user role:', userRole || user?.role, 'Is Admin:', isAdmin, 'Visible menus:', filteredMenuItems.length);
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', backgroundColor: '#f5f5f5' }} aria-label="Sidebar menu" role="navigation">
        <List sx={{ padding: 0 }}>
        {filteredMenuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={item.path && location.pathname === item.path}
                onClick={() => {
                  if (item.subItems) {
                    if (item.text === 'Invoice') setOpenInvoice(!openInvoice);
                    if (item.text === 'Admin') setOpenAdmin(!openAdmin);
                    if (item.text === 'Customer Request') setOpenCustomerRequest(!openCustomerRequest);
                  } else {
                    navigate(item.path);
                  }
                }}
                aria-label={`${item.text}${item.subItems ? ' menu' : ''}`}
                aria-expanded={item.subItems ? (
                  (item.text === 'Invoice' && openInvoice) ||
                  (item.text === 'Admin' && openAdmin) ||
                  (item.text === 'Customer Request' && openCustomerRequest)
                ) : undefined}
                aria-haspopup={item.subItems ? 'true' : undefined}
                aria-current={item.path && location.pathname === item.path ? 'page' : undefined}
                sx={{
                  minHeight: 48,
                  px: 2,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#e3f2fd',
                    '& .MuiListItemIcon-root': {
                      color: '#1976d2'
                    },
                    '& .MuiListItemText-primary': {
                      color: '#1976d2',
                      fontWeight: 500
                    }
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#1976d2',
                    '& .MuiListItemIcon-root': {
                      color: '#ffffff'
                    },
                    '& .MuiListItemText-primary': {
                      color: '#ffffff',
                      fontWeight: 600
                    },
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    }
                  },
                  '&:focus-visible': {
                    outline: '2px solid #1976d2',
                    outlineOffset: '-2px'
                  }
                }}
              >
                <ListItemIcon aria-hidden="true" sx={{ color: '#666666', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: '#333333'
                    }
                  }}
                />
                {item.subItems && (
                  (item.text === 'Invoice' && (openInvoice ? <ExpandLess aria-hidden="true" /> : <ExpandMore aria-hidden="true" />)) ||
                  (item.text === 'Admin' && (openAdmin ? <ExpandLess aria-hidden="true" /> : <ExpandMore aria-hidden="true" />)) ||
                  (item.text === 'Customer Request' && (openCustomerRequest ? <ExpandLess aria-hidden="true" /> : <ExpandMore aria-hidden="true" />))
                )}
              </ListItemButton>
            </ListItem>
            {item.subItems && item.text === 'Customer Request' && (
              <Collapse in={openCustomerRequest} timeout="auto" unmountOnExit>
                <List component="div" disablePadding aria-label="Customer Request submenu" role="menu">
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.text}
                      sx={{ pl: 4 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => navigate(subItem.path)}
                      aria-label={subItem.text}
                      aria-current={location.pathname === subItem.path ? 'page' : undefined}
                      role="menuitem"
                    >
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            {item.subItems && item.text === 'Invoice' && (
              <Collapse in={openInvoice} timeout="auto" unmountOnExit>
                <List component="div" disablePadding aria-label="Invoice submenu" role="menu">
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.text}
                      sx={{ pl: 4 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => navigate(subItem.path)}
                      aria-label={subItem.text}
                      aria-current={location.pathname === subItem.path ? 'page' : undefined}
                      role="menuitem"
                    >
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
            {item.subItems && item.text === 'Admin' && (
              <Collapse in={openAdmin} timeout="auto" unmountOnExit>
                <List component="div" disablePadding aria-label="Admin submenu" role="menu">
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.text}
                      sx={{ pl: 4 }}
                      selected={location.pathname === subItem.path}
                      onClick={() => navigate(subItem.path)}
                      aria-label={subItem.text}
                      aria-current={location.pathname === subItem.path ? 'page' : undefined}
                      role="menuitem"
                    >
                      <ListItemText primary={subItem.text} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
        </List>
    </Box>
  );
};

export default Sidebar;